import { useParams, Link } from 'react-router-dom';
import { useForumTopic, useForumTopicPosts } from '../hooks/use-api';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import { InfoBlock } from '../components/shared/InfoBlock';
import { formatRelativeTime } from '../hooks/use-relative-time';
import styles from './ForumTopicPage.module.css';

const ForumTopicPage = (): React.ReactElement => {
  const { topicId } = useParams();
  const safeId = topicId ?? '';

  const topicQuery = useForumTopic(safeId);
  const { items: posts, isLoading: postsLoading, hasMore: postsHasMore, loadMore: postsLoadMore, isFetchingNextPage: postsFetchingNext } = useForumTopicPosts(safeId);

  if (topicQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (topicQuery.isError || !topicQuery.data) {
    return (
      <div className={styles.page}>
        <EmptyState message="Topic not found" />
      </div>
    );
  }

  const topic = topicQuery.data;

  return (
    <div className={styles.page}>
      <button data-testid="back" className={styles.backButton} onClick={() => { window.history.back(); }} type="button">
        &larr; Back
      </button>

      <header className={styles.header}>
        <span className={`${styles.typeBadge} ${
          topic.type === 'wager' ? styles.badgeWager :
          topic.type === 'belief' ? styles.badgeBelief :
          styles.badgeDiscussion
        }`}>
          {topic.type}
        </span>
        {topic.sourceId && topic.type === 'wager' && (
          <Link to={`/wager/${topic.sourceId}`} className={styles.sourceLink}>
            View Wager →
          </Link>
        )}
        <h1 className={styles.topicTitle}>{topic.title}</h1>
        <div className={styles.headerMeta}>
          <Link to={`/entity/${topic.creatorId}`} className={styles.entityLink}>{topic.creatorName}</Link>
          <span className={styles.time}>{formatRelativeTime(topic.createdAt)}</span>
          <span className={styles.postCount}>{topic.postCount ?? 0} post{(topic.postCount ?? 0) !== 1 ? 's' : ''}</span>
        </div>
      </header>

      <InfoBlock title="Posts">
        {postsLoading ? (
          <Spinner />
        ) : posts.length === 0 ? (
          <EmptyState message="No posts yet" />
        ) : (
          <div className={styles.postList}>
            {posts.map((post, idx) => (
              <div key={idx} className={styles.post}>
                <div className={styles.postHeader}>
                  <Link to={`/entity/${post.entityId}`} className={styles.entityLink}>{post.entityName}</Link>
                  <span className={styles.postTime}>{formatRelativeTime(post.createdAt)}</span>
                  {post.postType != null && post.postType !== 'general' && (
                    <span className={styles.postTypeBadge}>{post.postType}</span>
                  )}
                </div>
                <div className={styles.postContent}>{post.content}</div>
              </div>
            ))}
            <LoadMoreButton onClick={postsLoadMore} isLoading={postsFetchingNext} hasMore={postsHasMore} />
          </div>
        )}
      </InfoBlock>
    </div>
  );
};

export { ForumTopicPage };
